// bluetooth.js
// Requires: cordova-plugin-ble-central

import toasts from "./toasts";
import log from "./log";
import logError from "./logError";

// Utility function to log with automatic object stringification

class TemperatureBluetooth {
	constructor() {
		this.SERVICE = 'ef090000-11d6-42ba-93b8-9dd7ec090ab0';

		this.CHAR = {
			deviceId: 'ef090001-11d6-42ba-93b8-9dd7ec090aa9',
			txPower: 'ef090003-11d6-42ba-93b8-9dd7ec090aa9',
			batteryMv: 'ef090007-11d6-42ba-93b8-9dd7ec090aa9',
			temperature: 'ef090080-11d6-42ba-93b8-9dd7ec090aa9',
			humidity: 'ef090081-11d6-42ba-93b8-9dd7ec090aa9',
			pressure: 'ef090082-11d6-42ba-93b8-9dd7ec090aa9',
		};
		this.devices = [];
		this.connectedDeviceId = null;
		this.refreshInterval = null;
		this.onTemperatureUpdate = null;
	}

	// --- Internal helpers ---
	readChar(deviceId, serviceUUID, characteristicUUID) {
		return new Promise((resolve, reject) => {
			window.ble.read(deviceId, serviceUUID, characteristicUUID, resolve, reject);
		});
	}

	// Subscribe to notifications on a characteristic
	subscribeToNotifications(deviceId, serviceUUID, characteristicUUID, onData) {
		return new Promise((resolve, reject) => {
			window.ble.startNotification(deviceId, serviceUUID, characteristicUUID,
				(data) => {
					log('[BLE] Notification received:', this.bytesToHex(data));
					onData(data);
				},
				(error) => {
					logError('[BLE] Notification error:', error);
					reject(error);
				}
			);

			// Resolve after a short delay to ensure subscription is active
			setTimeout(() => resolve(), 100);
		});
	}

	// Unsubscribe from notifications
	unsubscribeFromNotifications(deviceId, serviceUUID, characteristicUUID) {
		return new Promise((resolve, reject) => {
			window.ble.stopNotification(deviceId, serviceUUID, characteristicUUID,
				() => {
					log('[BLE] Unsubscribed from notifications');
					resolve();
				},
				(error) => {
					logError('[BLE] Unsubscribe error:', error);
					reject(error);
				}
			);
		});
	}

	// Discover device characteristics to find the correct trigger characteristic
	async discoverCharacteristics(deviceId) {
		try {
			log('[BLE] Discovering characteristics for device:', deviceId);
			
			// Get all services first
			window.ble.services(deviceId, (services) => {
				log('[BLE] Found services:', services);
				
				// For each service, get its characteristics
				services.forEach(service => {
					window.ble.characteristics(deviceId, service, (characteristics) => {
						log(`[BLE] Characteristics for service ${service}:`, characteristics);
						
						// Look for characteristics that support write operations
						characteristics.forEach(char => {
							if (char.properties && char.properties.write) {
								log('[BLE] Found writable characteristic:', char);
							}
						});
					}, (error) => {
						logError('[BLE] Error getting characteristics for service:', service, error);
					});
				});
			}, (error) => {
				logError('[BLE] Error getting services:', error);
			});
		} catch (error) {
			logError('[BLE] Error in discoverCharacteristics:', error);
		}
	}

	// Try to find the correct trigger characteristic
	async findTriggerCharacteristic(deviceId) {
		try {
			log('[BLE] Looking for trigger characteristic...');
			
			// Common trigger characteristic UUIDs for SensorPush devices
			const possibleTriggers = [
				'ef090002-11d6-42ba-93b8-9dd7ec090aa9', // Common trigger characteristic
				'ef090004-11d6-42ba-93b8-9dd7ec090aa9', // Alternative trigger
				'ef090005-11d6-42ba-93b8-9dd7ec090aa9', // Another possibility
			];
			
			for (const triggerUUID of possibleTriggers) {
				try {
					log('[BLE] Testing trigger characteristic:', triggerUUID);
					
					// Try to write a small value to test if it's writable
					const testValue = new Uint8Array([0x01]).buffer;
					
					await new Promise((resolve, reject) => {
						window.ble.write(deviceId, this.SERVICE, triggerUUID, testValue, 
							() => {
								log('[BLE] Successfully wrote to trigger characteristic:', triggerUUID);
								resolve(triggerUUID);
							},
							(error) => {
								log('[BLE] Failed to write to characteristic:', triggerUUID, error);
								reject(error);
							}
						);
					});
					
					return triggerUUID;
				} catch (error) {
					log('[BLE] Characteristic not writable:', triggerUUID);
					continue;
				}
			}
			
			logError('[BLE] No writable trigger characteristic found');
			return null;
		} catch (error) {
			logError('[BLE] Error finding trigger characteristic:', error);
			return null;
		}
	}

	async triggerAndReadTemp() {
		log('triggerAndReadTemp');
		
		if (!this.connectedDeviceId) {
			logError('No device connected');
			return;
		}
		
		try {
			// First, try to find the correct trigger characteristic
			const triggerChar = await this.findTriggerCharacteristic(this.connectedDeviceId);
			
			if (!triggerChar) {
				logError('No trigger characteristic found, trying direct read');
				// Fallback: just read the temperature directly
				const temperature = await this.readTemperature(this.connectedDeviceId);
				log('Direct temperature read:', temperature);
				toasts.show(`Temp: ${temperature.celsius.toFixed(1)}°C / ${temperature.fahrenheit.toFixed(1)}°F`);
				return;
			}
			
			// 4 bytes little-endian; any value is fine
			const trigger = new Uint8Array([0x01, 0x00, 0x00, 0x00]).buffer;
			log('triggerAndReadTemp - writing trigger to device', this.connectedDeviceId, this.SERVICE, triggerChar);
			
			// Use the correct trigger characteristic
			window.ble.write(this.connectedDeviceId, this.SERVICE, triggerChar, trigger, 
				(res) => {
					// Write successful
					log('window.ble.write callback - write successful', res);
					
					// small delay to let the sensor update (docs say <100ms)
					setTimeout(() => {
						log('Reading temperature after trigger...');
						window.ble.read(this.connectedDeviceId, this.SERVICE, this.CHAR.temperature, 
							(buffer) => {
								log('Read successful, buffer:', buffer);
								const dv = new DataView(buffer);
								const raw = dv.getInt32(0, true);   // little-endian
								const tempC = raw / 100.0;
								const tempF = tempC * 9/5 + 32;
								log('Temperature calculated:', { celsius: tempC, fahrenheit: tempF });
								toasts.show(`Temp: ${tempC.toFixed(1)}°C / ${tempF.toFixed(1)}°F`);
							}, 
							(err) => {
								logError('Read error after trigger:', err);
								toasts.error(`Read failed: ${err}`);
							}
						);
					}, 150);
				}, 
				(err) => {
					logError('Write error:', err);
					toasts.error(`Write failed: ${err}`);
				}
			);
			log('window.ble.write called successfully');
		} catch (error) {
			logError('Exception during write operation:', error);
			toasts.error(`Write exception: ${error.message}`);
		}
	}

	// Subscribe to temperature notifications
	subscribeToTemperatureNotifications(deviceId, onTemperatureUpdate) {
		return this.subscribeToNotifications(
			deviceId,
			this.SERVICE,
			this.CHAR.temperature,
			(data) => {
				try {
					const temperature = this.parseTemperatureFromBuffer(data);
					log('[BLE] Temperature notification:', temperature);
					onTemperatureUpdate(temperature);
				} catch (error) {
					logError('[BLE] Error parsing temperature notification:', error);
				}
			}
		);
	}

	// Parse temperature from buffer
	parseTemperatureFromBuffer(buffer) {
		const value = buffer.byteLength === 2
			? this.parseScaled(buffer, {bytes: 2, signed: true, scale: 100})
			: this.parseScaled(buffer, {bytes: 4, signed: true, scale: 100});
		return {celsius: value, fahrenheit: value * 9 / 5 + 32};
	}

	parseScaled(buffer, {bytes = 2, signed = true, scale = 100}) {
		const dv = new DataView(buffer);
		let raw;
		if (bytes === 2) {
			raw = signed ? dv.getInt16(0, true) : dv.getUint16(0, true);
		} else {
			raw = signed ? dv.getInt32(0, true) : dv.getUint32(0, true);
		}
		return raw / scale;
	}

	batteryPercentFromMv(mv) {
		const min = 3000, max = 4200;
		const pct = Math.max(0, Math.min(1, (mv - min) / (max - min)));
		return Math.round(pct * 100);
	}

	bytesToHex(ab) {
		if (!ab || !ab.byteLength) return '';
		const u8 = new Uint8Array(ab);
		return Array.from(u8).map(b => b.toString(16).padStart(2, '0')).join('');
	}

	logPeripheral(peripheral) {
		this.devices.push({...peripheral, name: peripheral.name || 'no name'});
		log('[BLE] Found device:', JSON.stringify({
			name: peripheral.name || '(no name)',
			id: peripheral.id,
			rssi: peripheral.rssi,
			services: peripheral.services,
			advertising: peripheral.advertising
		}, null, 1));
	}

	getBestMatch = () => {
		const bestDevice = this.devices.filter(x => x.name.startsWith('SensorPush')).reduce((best, current) => {
			return current.rssi > best.rssi ? current : best;
		});

		log('[BLE] Best match found:', bestDevice.name, 'RSSI:', bestDevice.rssi);
		return bestDevice;
	};

	// Find best match and connect to read temperature
	async findAndConnectBestMatch() {
		try {
			log('[BLE] Finding and connecting to best SensorPush device...');

			// Find the best matching device
			const bestDevice = this.getBestMatch();

			// Connect to the device
			log('[BLE] Connecting to:', bestDevice.name);
			await this.connect(bestDevice.id);

			// Read temperature
			log('[BLE] Reading temperature from:', bestDevice.name);
			const temperature = await this.readTemperature(bestDevice.id);
			log('[BLE] temperature:', temperature);

			return {
				device: bestDevice,
				temperature: temperature
			};
		} catch (error) {
			logError('[BLE] Error in findAndConnectBestMatch:', error);
			throw error;
		}
	}

	// --- Refresh and Continuous Monitoring ---

	// Refresh temperature data for currently connected device
	async refreshTemperature() {
		if (!this.connectedDeviceId) {
			throw new Error('No device connected. Please connect to a device first.');
		}

		try {
			log('[BLE] Refreshing temperature data...');
			const temperature = await this.readTemperature(this.connectedDeviceId);
			log('[BLE] Refreshed temperature:', temperature);

			// Call callback if set
			if (this.onTemperatureUpdate) {
				this.onTemperatureUpdate(temperature);
			}

			return temperature;
		} catch (error) {
			logError('[BLE] Error refreshing temperature:', error);
			throw error;
		}
	}

	// Force fresh temperature reading by disconnecting and reconnecting
	async forceFreshTemperature(deviceId) {
		try {
			log('[BLE] Forcing fresh temperature reading...');

			// Disconnect if currently connected
			if (this.connectedDeviceId) {
				log('[BLE] Disconnecting from current device...');
				await this.disconnect();
			}

			// Wait a moment for the device to reset
			await new Promise(resolve => setTimeout(resolve, 1000));

			// Reconnect to the device
			log('[BLE] Reconnecting to device for fresh reading...');
			await this.connect(deviceId);

			// Wait a moment for the connection to stabilize
			await new Promise(resolve => setTimeout(resolve, 500));

			// Read temperature
			log('[BLE] Reading fresh temperature...');
			const temperature = await this.readTemperature(deviceId);
			log('[BLE] Fresh temperature reading:', temperature);

			return temperature;
		} catch (error) {
			logError('[BLE] Error forcing fresh temperature:', error);
			throw error;
		}
	}

	// Read temperature with detailed debugging
	async readTemperatureWithDebug(deviceId) {
		try {
			log('[BLE] Reading temperature with debug info...');
			const buf = await this.readChar(deviceId, this.SERVICE, this.CHAR.temperature);

			// Log raw buffer data
			log('[BLE] Raw temperature buffer:', this.bytesToHex(buf));
			log('[BLE] Buffer length:', buf.byteLength);
			log('[BLE] Buffer as array:', Array.from(new Uint8Array(buf)));

			const value = buf.byteLength === 2
				? this.parseScaled(buf, {bytes: 2, signed: true, scale: 100})
				: this.parseScaled(buf, {bytes: 4, signed: true, scale: 100});

			const result = {celsius: value, fahrenheit: value * 9 / 5 + 32};
			log('[BLE] Parsed temperature result:', result);

			return result;
		} catch (error) {
			logError('[BLE] Error reading temperature with debug:', error);
			throw error;
		}
	}

	// Read all sensor data for debugging
	async readAllWithDebug(deviceId) {
		try {
			log('[BLE] Reading all sensor data with debug...');

			const [bat, temp, hum, pres] = await Promise.allSettled([
				this.readBattery(deviceId),
				this.readTemperatureWithDebug(deviceId),
				this.readHumidity(deviceId),
				this.readPressure(deviceId),
			]);

			const result = {
				battery: bat.value || null,
				temperature: temp.value || null,
				humidity: hum.value || null,
				pressure: pres.value || null,
			};

			log('[BLE] All sensor data:', result);
			return result;
		} catch (error) {
			logError('[BLE] Error reading all sensor data:', error);
			throw error;
		}
	}

	// Start continuous temperature monitoring
	startContinuousMonitoring(deviceId, intervalSeconds = 5, onUpdate = null) {
		return new Promise(async (resolve, reject) => {
			try {
				// Connect to device if not already connected
				if (this.connectedDeviceId !== deviceId) {
					await this.connect(deviceId);
					this.connectedDeviceId = deviceId;
				}

				// Set up callback
				this.onTemperatureUpdate = onUpdate;

				// Clear any existing interval
				if (this.refreshInterval) {
					clearInterval(this.refreshInterval);
				}

				// Start periodic refresh
				this.refreshInterval = setInterval(async () => {
					try {
						await this.refreshTemperature();
					} catch (error) {
						logError('[BLE] Error in continuous monitoring:', error);
						// Stop monitoring on error
						this.stopContinuousMonitoring();
					}
				}, intervalSeconds * 1000);

				log(`[BLE] Started continuous monitoring every ${intervalSeconds} seconds`);
				resolve();
			} catch (error) {
				logError('[BLE] Error starting continuous monitoring:', error);
				reject(error);
			}
		});
	}

	// Stop continuous temperature monitoring
	stopContinuousMonitoring() {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
			log('[BLE] Stopped continuous monitoring');
		}
		this.onTemperatureUpdate = null;
	}

	// Disconnect from current device
	disconnect() {
		return new Promise(async (resolve, reject) => {
			if (!this.connectedDeviceId) {
				resolve();
				return;
			}

			try {
				// Stop monitoring first
				await this.stopContinuousMonitoring();

				// Disconnect from device
				window.ble.disconnect(this.connectedDeviceId, () => {
					log('[BLE] Disconnected from device:', this.connectedDeviceId);
					this.connectedDeviceId = null;
					resolve();
				}, (error) => {
					logError('[BLE] Error disconnecting:', error);
					this.connectedDeviceId = null;
					reject(error);
				});
			} catch (error) {
				logError('[BLE] Error in disconnect process:', error);
				this.connectedDeviceId = null;
				reject(error);
			}
		});
	}

	// Get current connection status
	isConnected() {
		return this.connectedDeviceId !== null;
	}

	// Get connected device ID
	getConnectedDeviceId() {
		return this.connectedDeviceId;
	}

	// --- Public API ---
	requestPermissions() {
		return new Promise((resolve, reject) => {
			if (!window.ble) {
				reject(new Error('Bluetooth not available'));
				return;
			}

			log('[BLE] Starting permission request process...');
			log('[BLE] Available BLE methods:', Object.keys(window.ble));
			log('[BLE] iOS version:', navigator.userAgent);

			// First check if Bluetooth is enabled
			window.ble.isEnabled((enabled) => {
				if (!enabled) {
					log('[BLE] Bluetooth is disabled - cannot request permissions');
					reject(new Error('Bluetooth is disabled'));
					return;
				}

				log('[BLE] Bluetooth is enabled, attempting to trigger permission prompt...');

				// Try multiple approaches to trigger the permission prompt
				const onDevice = (device) => {
					log('[BLE] Permission scan successful, found device:', device.id);
					log('[BLE] If you didn\'t see a permission prompt, permissions may already be granted');
				};

				const onError = (error) => {
					log('[BLE] Permission scan error:', error);
					log('[BLE] Error details:', JSON.stringify(error));
					// Even if scan fails, the permission prompt should have been triggered
					resolve(true);
				};

				// Try multiple scanning methods to trigger permissions
				const tryScan = () => {
					this.devices = [];
					// Method 1: Try startScanWithOptions (iOS preferred)
					if (typeof window.ble.startScanWithOptions === 'function') {
						log('[BLE] Trying startScanWithOptions...');
						try {
							window.ble.startScanWithOptions([], {reportDuplicates: false}, onDevice, onError);
						} catch (e) {
							log('[BLE] startScanWithOptions failed:', e);
							// Fall back to regular startScan
							if (typeof window.ble.startScan === 'function') {
								log('[BLE] Falling back to startScan...');
								window.ble.startScan([], onDevice, onError);
							}
						}
					}
					// Method 2: Try regular startScan
					else if (typeof window.ble.startScan === 'function') {
						log('[BLE] Trying startScan...');
						window.ble.startScan([], onDevice, onError);
					}
					// Method 3: Try with specific service UUID
					else {
						log('[BLE] Trying startScan with service UUID...');
						window.ble.startScan(['1800'], onDevice, onError);
					}
				};

				// Start the scan
				tryScan();

				// Stop scan after 10 seconds to give more time for permission prompt
				setTimeout(() => {
					window.ble.stopScan(() => {
						log('[BLE] Permission scan completed');
						log('[BLE] If no permission prompt appeared, check:');
						log('[BLE] 1. App permissions in Settings');
						log('[BLE] 2. BLE plugin configuration');
						log('[BLE] 3. iOS version compatibility');
						log('[BLE] 4. Try restarting the app');
						resolve(true);
					}, onError);
				}, 10000);
			}, (error) => {
				log('[BLE] Error checking if Bluetooth is enabled:', error);
				reject(new Error('Bluetooth is disabled'));
			});
		});
	}

	isEnabled() {
		return new Promise((resolve, reject) => {
			if (!window.ble) {
				resolve(false);
				return;
			}

			window.ble.isEnabled((enabled) => {
				resolve(enabled);
			}, (error) => {
				log('[BLE] Error checking if Bluetooth is enabled:', error);
				resolve(false);
			});
		});
	}

	// Check if we have permission to use Bluetooth
	checkPermissionStatus() {
		return new Promise((resolve, reject) => {
			if (!window.ble) {
				resolve('unavailable');
				return;
			}

			// Try a very brief scan to test permissions
			const onDevice = (device) => {
				log('[BLE] Permission test successful');
				resolve('granted');
			};

			const onError = (error) => {
				log('[BLE] Permission test failed:', error);
				if (error && error.includes && error.includes('permission')) {
					resolve('denied');
				} else {
					resolve('unknown');
				}
			};

			// Start a very brief test scan
			window.ble.startScan([], onDevice, onError);

			// Stop after 1 second
			setTimeout(() => {
				window.ble.stopScan(() => {
					resolve('unknown');
				}, onError);
			}, 1000);
		});
	}

	connect(deviceId) {
		return new Promise((resolve, reject) => {
			window.ble.connect(deviceId, peripheral => {
				this.connectedDeviceId = deviceId;
				log('[BLE] Connected to device:', deviceId);
				resolve(peripheral);
			}, err => reject(err));
		});
	}

	// Connect and subscribe to temperature notifications with fallback to reading
	async connectAndSubscribeToTemperature(deviceId, onTemperatureUpdate) {
		try {
			log('[BLE] Connecting and attempting to subscribe to temperature notifications...');

			// Connect to device
			await this.connect(deviceId);

			// Try to subscribe to temperature notifications
			try {
				await this.subscribeToTemperatureNotifications(deviceId, onTemperatureUpdate);
				log('[BLE] Successfully subscribed to temperature notifications');
				return {method: 'notifications', success: true};
			} catch (notificationError) {
				log('[BLE] Notifications not supported, falling back to reading:', notificationError.message);

				// Fallback: Read temperature immediately and set up polling
				const temperature = await this.readTemperature(deviceId);
				onTemperatureUpdate(temperature);

				// Set up polling for regular updates
				this.getTemp(deviceId, onTemperatureUpdate);

				return {method: 'polling', success: true};
			}
		} catch (error) {
			logError('[BLE] Error connecting and subscribing:', error);
			throw error;
		}
	}

	// Setup temperature polling as fallback when notifications aren't supported
	async getTemp(deviceId, onTemperatureUpdate, intervalSeconds = 5) {
		log(`[BLE] getTemp`);

		// Start polling
		try {
			if (this.connectedDeviceId === deviceId) {
				const temperature = await this.readTemperature(deviceId);
				log('[BLE] Polled temperature:', temperature);
				onTemperatureUpdate(temperature);
			}
		} catch (error) {
			logError('[BLE] Error in temperature polling:', error);
		}
	}

	// Find best match and connect with temperature monitoring (notifications or polling)
	async findAndConnectBestMatchWithMonitoring(onTemperatureUpdate) {
		try {
			log('[BLE] Finding and connecting to best SensorPush device with monitoring...');

			// Find the best matching device
			const bestDevice = this.getBestMatch();

			// Connect and monitor temperature (notifications or polling)
			const result = await this.connectAndSubscribeToTemperature(bestDevice.id, onTemperatureUpdate);

			return {
				device: bestDevice,
				method: result.method,
				success: true
			};
		} catch (error) {
			logError('[BLE] Error in findAndConnectBestMatchWithMonitoring:', error);
			throw error;
		}
	}

	// Start continuous temperature monitoring (notifications or polling)
	startContinuousMonitoringWithFallback(deviceId, onUpdate = null, pollingInterval = 5) {
		return new Promise(async (resolve, reject) => {
			try {
				// Connect and monitor temperature
				const result = await this.connectAndSubscribeToTemperature(deviceId, (temperature) => {
					log('[BLE] Temperature update received:', temperature);
					if (onUpdate) {
						onUpdate(temperature);
					}
				});

				log(`[BLE] Started continuous monitoring using ${result.method}`);
				resolve(result);
			} catch (error) {
				logError('[BLE] Error starting continuous monitoring:', error);
				reject(error);
			}
		});
	}

	// Stop continuous monitoring and unsubscribe

	async readBattery(deviceId) {
		const buf = await this.readChar(deviceId, this.SERVICE, this.CHAR.batteryMv);
		const dv = new DataView(buf);
		const mv = buf.byteLength >= 4 ? dv.getUint32(0, true) : dv.getUint16(0, true);
		return {millivolts: mv, percent: this.batteryPercentFromMv(mv)};
	}

	async readTemperature(deviceId) {
		const buf = await this.readChar(deviceId, this.SERVICE, this.CHAR.temperature);
		const value = buf.byteLength === 2
			? this.parseScaled(buf, {bytes: 2, signed: true, scale: 100})
			: this.parseScaled(buf, {bytes: 4, signed: true, scale: 100});
		return {celsius: value, fahrenheit: value * 9 / 5 + 32};
	}

	async readHumidity(deviceId) {
		const buf = await this.readChar(deviceId, this.SERVICE, this.CHAR.humidity);
		const value = buf.byteLength === 2
			? this.parseScaled(buf, {bytes: 2, signed: false, scale: 100})
			: this.parseScaled(buf, {bytes: 4, signed: false, scale: 100});
		return {percent: value};
	}

	async readPressure(deviceId) {
		const buf = await this.readChar(deviceId, this.SERVICE, this.CHAR.pressure);
		const pa = buf.byteLength === 2
			? this.parseScaled(buf, {bytes: 2, signed: false, scale: 100})
			: this.parseScaled(buf, {bytes: 4, signed: false, scale: 100});
		return {pascals: pa, hPa: pa / 100, mbar: pa / 100};
	}

	async readAll(deviceId) {
		const [bat, temp, hum, pres] = await Promise.allSettled([
			this.readBattery(deviceId),
			this.readTemperature(deviceId),
			this.readHumidity(deviceId),
			this.readPressure(deviceId),
		]);
		return {
			battery: bat.value || null,
			temperature: temp.value || null,
			humidity: hum.value || null,
			pressure: pres.value || null,
		};
	}

	scanAndLog({seconds = 10, reportDuplicates = false, serviceFilter = []} = {}) {
		return new Promise((resolve, reject) => {
			log(`[BLE] Scanning for ${seconds}s…`);
			const seen = new Set();
			const onDevice = (p) => {
				if (!reportDuplicates && seen.has(p.id)) return;
				seen.add(p.id);
				this.logPeripheral(p);
			};
			const start = (cb, err) => {
				if (typeof window.ble.startScanWithOptions === 'function') {
					log('window.ble.startScanWithOptions(serviceFilter, { reportDuplicates }, cb, err);')
					window.ble.startScanWithOptions(serviceFilter, {reportDuplicates}, cb, err);
				} else {
					log('window.ble.startScan(serviceFilter, cb, err);')
					window.ble.startScan(serviceFilter, cb, err);
				}
			};
			start(onDevice, reject);
			setTimeout(() => {
				window.ble.stopScan(() => resolve(Array.from(seen)), reject);
			}, seconds * 1000);
		});
	}
}

// Export a single instance
export default new TemperatureBluetooth();
